import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router";
import { useI18n } from "@wato/i18n/react";
import { Alert, Button, Input, Select, Stack } from "@wato/ui";
import { useApi, useErrorMessage } from "../../api/context";
import type { WorkspaceCountry } from "../../api/types";
import { AuthLayout, PageLoading } from "../../components/common";
import type { LegalDocumentView } from "../../api/types";
import { allAccepted, LegalAcceptance, refKey } from "../../pages/signup/legal-acceptance";
import { rememberWorkspaceId } from "../workspace-switcher/last-workspace";

/** Country-derived defaults shown before creation (the API applies the same rule, FR-WS-002). */
const COUNTRY_PREVIEW: Readonly<Record<WorkspaceCountry, { currency: string; timezone: string }>> =
  {
    SA: { currency: "SAR", timezone: "Asia/Riyadh" },
    BH: { currency: "BHD", timezone: "Asia/Bahrain" },
  };

/**
 * "Create your workspace" — first step after sign-up (pre-filled from the sign-up answers) and
 * the switcher's "Create workspace". On success the creator (now Owner) lands on the new
 * workspace's onboarding wizard.
 */
export function CreateWorkspacePage({ first = false }: { first?: boolean }) {
  const { t, locale } = useI18n();
  const api = useApi();
  const navigate = useNavigate();
  const errorMessage = useErrorMessage();
  const [loading, setLoading] = useState(first);
  const [name, setName] = useState("");
  const [country, setCountry] = useState<WorkspaceCountry>("SA");
  const [legalDocs, setLegalDocs] = useState<readonly LegalDocumentView[]>([]);
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!first) return undefined;
    let active = true;
    api
      .workspaceDefaults()
      .then((defaults) => {
        if (!active) return;
        if (defaults.businessName) setName(defaults.businessName);
        setCountry(defaults.country);
      })
      .catch(() => undefined) // Pre-fill is a convenience only.
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [api, first]);

  useEffect(() => {
    let active = true;
    api
      .requiredLegalDocuments()
      .then((docs) => active && setLegalDocs(docs))
      .catch(() => undefined); // The server still rejects a missing acceptance (422).
    return () => {
      active = false;
    };
  }, [api]);

  function toggleLegal(doc: LegalDocumentView) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (!next.delete(refKey(doc))) next.add(refKey(doc));
      return next;
    });
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const workspace = await api.createWorkspace({
        name: name.trim(),
        country,
        localeDefault: locale,
        legalAcceptance: legalDocs.map((d) => ({ documentKey: d.documentKey, version: d.version })),
      });
      rememberWorkspaceId(workspace.id);
      await navigate(`/w/${workspace.id}/onboarding`, { replace: true });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <PageLoading />;
  const preview = COUNTRY_PREVIEW[country];

  return (
    <AuthLayout
      title={t("dashboard.workspace.create.title")}
      subtitle={t("dashboard.workspace.create.subtitle")}
    >
      <form onSubmit={submit} noValidate>
        <Stack gap={4}>
          {error && <Alert tone="error">{error}</Alert>}
          <Input
            required
            label={t("dashboard.workspace.create.name")}
            hint={t("dashboard.workspace.create.nameHint")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Select
            label={t("dashboard.workspace.create.country")}
            hint={t("dashboard.workspace.create.countryHint")}
            value={country}
            onChange={(e) => setCountry(e.target.value as WorkspaceCountry)}
            options={(["SA", "BH"] as const).map((c) => ({
              value: c,
              label: t(`dashboard.workspace.create.countries.${c}`),
            }))}
          />
          <p className="text-sm text-fg-muted" data-testid="country-preview">
            {t("dashboard.workspace.create.summary", {
              currency: preview.currency,
              timezone: preview.timezone,
            })}
          </p>
          {legalDocs.length > 0 && (
            <LegalAcceptance documents={legalDocs} checked={checked} onToggle={toggleLegal} />
          )}
          <Button
            type="submit"
            fullWidth
            loading={busy}
            disabled={!allAccepted(legalDocs, checked)}
          >
            {t("dashboard.workspace.create.submit")}
          </Button>
        </Stack>
      </form>
    </AuthLayout>
  );
}
